import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, ChevronLeft, MapPin, Footprints, Droplets, 
  Shield, Palette, Check, RotateCcw, ArrowRight, Trophy,
  Building, Warehouse, ShoppingCart, Coffee, GraduationCap, Landmark
} from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { products } from '@/data/products';
import { cn } from '@/lib/utils';
import SEO from '@/components/shared/SEO';

// Questions for the guided selector
const questions = [
  {
    id: 'location',
    title: 'Where will the flooring be installed?',
    subtitle: 'Select the primary installation zone',
    icon: MapPin,
    options: [
      { 
        id: 'zone1', 
        label: 'Zone 1 - Exterior', 
        description: 'Outdoor entrances, walkways, parking areas',
        icon: '🏢',
        zones: [1]
      },
      { 
        id: 'zone2', 
        label: 'Zone 2 - Transition', 
        description: 'Vestibules, entryways, lobbies',
        icon: '🚪',
        zones: [2]
      },
      { 
        id: 'zone3', 
        label: 'Zone 3 - Interior', 
        description: 'Corridors, offices, retail floors',
        icon: '🏬',
        zones: [3]
      },
      { 
        id: 'all', 
        label: 'Multiple Zones', 
        description: 'I need a comprehensive solution',
        icon: '🔄',
        zones: [1, 2, 3]
      }
    ]
  },
  {
    id: 'traffic',
    title: 'What level of foot traffic do you expect?',
    subtitle: 'This helps us recommend the right durability level',
    icon: Footprints,
    options: [
      { 
        id: 'light', 
        label: 'Light Traffic', 
        description: 'Small offices, private areas',
        trafficRatings: ['Low', 'Low-Medium']
      },
      { 
        id: 'medium', 
        label: 'Medium Traffic', 
        description: 'Standard commercial spaces',
        trafficRatings: ['Medium', 'Medium-High']
      },
      { 
        id: 'heavy', 
        label: 'Heavy Traffic', 
        description: 'High-volume retail, public buildings',
        trafficRatings: ['High', 'Heavy']
      },
      { 
        id: 'extreme', 
        label: 'Extreme Traffic', 
        description: 'Main entrances, transit hubs',
        trafficRatings: ['Extreme', 'Heavy to Extreme']
      }
    ]
  },
  {
    id: 'moisture',
    title: 'Will the flooring be exposed to moisture?',
    subtitle: 'Consider cleaning methods and environmental conditions',
    icon: Droplets,
    options: [
      { 
        id: 'dry', 
        label: 'Dry Environment', 
        description: 'Climate-controlled indoor spaces',
        moisture: 'low'
      },
      { 
        id: 'occasional', 
        label: 'Occasional Moisture', 
        description: 'Entry points, regular cleaning',
        moisture: 'medium'
      },
      { 
        id: 'wet', 
        label: 'Wet Environment', 
        description: 'Food service, bathrooms, pools',
        moisture: 'high'
      }
    ]
  },
  {
    id: 'industry',
    title: 'What type of facility is this for?',
    subtitle: 'Different industries have unique flooring requirements',
    icon: Building,
    options: [
      { 
        id: 'corporate', 
        label: 'Corporate Office', 
        description: 'Professional office environments',
        icon: Building,
        industries: ['Corporate Offices']
      },
      { 
        id: 'retail', 
        label: 'Retail Store', 
        description: 'Shops, malls, showrooms',
        icon: ShoppingCart,
        industries: ['Retailers', 'Mass Merchandiser']
      },
      { 
        id: 'hospitality', 
        label: 'Hospitality', 
        description: 'Hotels, restaurants, venues',
        icon: Coffee,
        industries: ['Hospitality Sector']
      },
      { 
        id: 'education', 
        label: 'Education/Government', 
        description: 'Schools, universities, public buildings',
        icon: GraduationCap,
        industries: ['Educational Institutions', 'Government']
      },
      { 
        id: 'grocery', 
        label: 'Grocery/Food Service', 
        description: 'Supermarkets, convenience stores',
        icon: Warehouse,
        industries: ['Grocery & Convenience Stores']
      },
      { 
        id: 'healthcare', 
        label: 'Healthcare/Financial', 
        description: 'Hospitals, clinics, banks',
        icon: Landmark,
        industries: ['Financial Institutions', 'Pharmacy & Drugstore']
      }
    ]
  }
];

// Recommendation algorithm
const getRecommendations = (answers) => {
  let scoredProducts = products
    .filter(p => !p.isPlaceholder && p.comparisonSpecs)
    .map(product => {
      let score = 0;
      let matchReasons = [];
      
      // Zone matching
      if (answers.location) {
        const selectedZones = questions[0].options.find(o => o.id === answers.location)?.zones || [];
        const productZones = product.zones || [];
        const zoneMatch = selectedZones.some(z => productZones.includes(z));
        if (zoneMatch) {
          score += 30;
          matchReasons.push('Zone compatible');
        }
      }
      
      // Traffic rating matching
      if (answers.traffic && product.comparisonSpecs?.trafficRating) {
        const selectedTraffic = questions[1].options.find(o => o.id === answers.traffic);
        const productRating = product.comparisonSpecs.trafficRating.toLowerCase();
        const matchesTraffic = selectedTraffic?.trafficRatings?.some(r => 
          productRating.includes(r.toLowerCase())
        );
        if (matchesTraffic) {
          score += 25;
          matchReasons.push('Traffic rating match');
        }
      }
      
      // Industry matching
      if (answers.industry && product.industries) {
        const selectedIndustry = questions[3].options.find(o => o.id === answers.industry);
        const industryMatch = selectedIndustry?.industries?.some(ind => 
          product.industries.includes(ind)
        );
        if (industryMatch) {
          score += 20;
          matchReasons.push('Industry suited');
        }
      }
      
      // Slip resistance bonus for wet environments
      if (answers.moisture === 'wet' && product.comparisonSpecs?.slipResistance === 'Yes') {
        score += 15;
        matchReasons.push('Slip resistant');
      }
      
      return {
        ...product,
        score,
        matchReasons
      };
    })
    .filter(p => p.score > 0)
    .sort((a, b) => b.score - a.score);
  
  // Get top recommendations by category
  const bestOverall = scoredProducts[0];
  const bestHeavyTraffic = scoredProducts.find(p => 
    p.comparisonSpecs?.trafficRating?.toLowerCase().includes('high') ||
    p.comparisonSpecs?.trafficRating?.toLowerCase().includes('extreme')
  );
  const bestMoistureResistant = scoredProducts.find(p => 
    p.comparisonSpecs?.slipResistance === 'Yes'
  );
  const bestValue = scoredProducts.find(p => 
    p.budgetLevels?.includes('economy') || p.budgetLevels?.includes('mid-range')
  );
  
  return {
    all: scoredProducts.slice(0, 8),
    bestOverall,
    bestHeavyTraffic,
    bestMoistureResistant,
    bestValue
  };
};

const GuidedSelectorPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  
  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;
  
  const handleAnswer = (optionId) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: optionId
    }));
    
    // Auto-advance to next question or show results
    setTimeout(() => {
      if (currentStep < questions.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        setShowResults(true);
      }
    }, 300);
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleReset = () => {
    setCurrentStep(0);
    setAnswers({});
    setShowResults(false);
  };
  
  const recommendations = showResults ? getRecommendations(answers) : null;
  
  const compareTopProducts = () => {
    if (recommendations?.all?.length >= 2) {
      const slugs = recommendations.all.slice(0, 4).map(p => p.slug).join(',');
      navigate(`/compare?products=${slugs}`);
    }
  };
  
  return (
    <Layout>
      <SEO 
        title="Guided Flooring Selector"
        description="Answer a few simple questions and get personalized commercial flooring recommendations. Our guided selector helps you find the perfect flooring for your zone, traffic level, and industry."
        canonicalUrl="/flooring-selector"
      />
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
        {/* Hero Section */}
        <section className="bg-primary text-primary-foreground py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                Guided Flooring Selector
              </h1>
              <p className="text-lg opacity-90">
                Answer a few questions and we'll recommend the best flooring solutions for your needs
              </p>
            </div>
          </div>
        </section>
        
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {!showResults ? (
            <>
              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex justify-between text-sm text-muted-foreground mb-2">
                  <span>Question {currentStep + 1} of {questions.length}</span>
                  <span>{Math.round(progress)}% Complete</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
              
              {/* Question Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="bg-card border border-border rounded-2xl p-8"
                >
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <currentQuestion.icon className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold mb-2">{currentQuestion.title}</h2>
                    <p className="text-muted-foreground">{currentQuestion.subtitle}</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {currentQuestion.options.map((option) => {
                      const isSelected = answers[currentQuestion.id] === option.id;
                      const OptionIcon = option.icon;
                      
                      return (
                        <button
                          key={option.id}
                          onClick={() => handleAnswer(option.id)}
                          className={cn(
                            "p-6 rounded-xl border-2 text-left transition-all duration-200",
                            "hover:border-primary hover:shadow-md",
                            isSelected 
                              ? "border-primary bg-primary/5 shadow-md" 
                              : "border-border"
                          )}
                        >
                          <div className="flex items-start gap-4">
                            <div className={cn(
                              "w-12 h-12 rounded-lg flex items-center justify-center text-2xl flex-shrink-0",
                              isSelected ? "bg-primary/20" : "bg-muted"
                            )}>
                              {typeof OptionIcon === 'string' ? OptionIcon : 
                                (OptionIcon ? <OptionIcon className="w-6 h-6" /> : option.label.charAt(0))}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold">{option.label}</h3>
                                {isSelected && (
                                  <Check className="w-5 h-5 text-primary" />
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Navigation */}
                  <div className="flex justify-between mt-8">
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      disabled={currentStep === 0}
                      className="gap-2"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Back
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={handleReset}
                      className="gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Start Over
                    </Button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </>
          ) : (
            /* Results Section */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
            >
              {/* Summary */}
              <div className="bg-card border border-border rounded-2xl p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Your Recommendations Are Ready!</h2>
                <p className="text-muted-foreground mb-6">
                  Based on your selections, we've found {recommendations?.all?.length || 0} products that match your needs.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button onClick={compareTopProducts} className="gap-2">
                    Compare Top Products
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" onClick={handleReset} className="gap-2">
                    <RotateCcw className="w-4 h-4" />
                    Start Over
                  </Button>
                </div>
              </div>
              
              {/* Top Recommendations */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { key: 'bestOverall', label: 'Best Overall', icon: Trophy, color: 'text-yellow-600' },
                  { key: 'bestHeavyTraffic', label: 'Best for Heavy Traffic', icon: Footprints, color: 'text-blue-600' },
                  { key: 'bestMoistureResistant', label: 'Best Moisture Resistant', icon: Droplets, color: 'text-cyan-600' },
                  { key: 'bestValue', label: 'Best Value', icon: Shield, color: 'text-green-600' }
                ].map(({ key, label, icon: Icon, color }) => {
                  const product = recommendations?.[key];
                  if (!product) return null;
                  
                  return (
                    <div key={key} className="bg-card border border-border rounded-xl overflow-hidden">
                      <div className={cn("px-4 py-2 flex items-center gap-2", 
                        key === 'bestOverall' ? "bg-yellow-50" : "bg-muted/50"
                      )}>
                        <Icon className={cn("w-4 h-4", color)} />
                        <span className="text-sm font-medium">{label}</span>
                      </div>
                      <div className="p-4">
                        <div className="aspect-[4/3] bg-muted rounded-lg overflow-hidden mb-3">
                          <img 
                            src={product.image} 
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <h3 className="font-semibold text-sm line-clamp-2 mb-1">{product.name}</h3>
                        <p className="text-xs text-muted-foreground mb-3">{product.category}</p>
                        <Link to={`/products/${product.slug}`}>
                          <Button size="sm" variant="outline" className="w-full">
                            View Details
                          </Button>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* All Recommendations */}
              <div className="bg-card border border-border rounded-2xl p-6">
                <h3 className="text-lg font-semibold mb-4">All Matching Products</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {recommendations?.all?.map((product) => (
                    <Link 
                      key={product.id} 
                      to={`/products/${product.slug}`}
                      className="group"
                    >
                      <div className="aspect-square bg-muted rounded-lg overflow-hidden mb-2">
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                      <h4 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">
                        {product.name}
                      </h4>
                      <p className="text-xs text-muted-foreground">{product.category}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {product.matchReasons?.slice(0, 2).map((reason, i) => (
                          <span key={i} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                            {reason}
                          </span>
                        ))}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
              
              {/* CTA */}
              <div className="text-center">
                <p className="text-muted-foreground mb-4">
                  Need expert guidance? Our team can help you make the right choice.
                </p>
                <Link to="/contact">
                  <Button variant="outline" className="gap-2">
                    Contact Our Experts
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default GuidedSelectorPage;
